import React, { useState, useEffect } from 'react';
import { Container, Table, Card, Row, Col, Badge, Button, Modal, Form, Spinner, Pagination, InputGroup, FormControl, DropdownButton, Dropdown } from 'react-bootstrap';
import Navbarpro from './sidebar';
import { api } from '../../../config';

function CustomerManagement() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [blockReason, setBlockReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        setLoading(true);
        try {
            // Try new admin endpoint first
            let data;
            try {
                const res = await api.get('/admin/customers');
                data = res.data.customers || [];
            } catch {
                // Fallback - no customer list endpoint exists in legacy
                data = [];
            }
            setCustomers(data);
        } catch (err) {
            console.error('Error:', err);
            setError('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    // Filter customers
    const filteredCustomers = customers.filter(customer => {
        const matchesSearch = !searchTerm ||
            customer.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customer.Phone?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = !statusFilter || customer.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

    const handleViewDetails = async (customer) => {
        try {
            const res = await api.get(`/admin/customers/${customer._id}`);
            setSelectedCustomer(res.data);
        } catch {
            setSelectedCustomer({ customer, orders: [], bookings: [], stats: {} });
        }
        setShowModal(true);
    };

    const handleBlockCustomer = async () => {
        if (!selectedCustomer) return;
        setActionLoading(true);
        try {
            const newStatus = selectedCustomer.customer.status === 'blocked' ? 'active' : 'blocked';
            await api.put(`/admin/customers/${selectedCustomer.customer._id}/status`, {
                status: newStatus,
                reason: blockReason
            });
            setSuccess(`Customer ${newStatus === 'blocked' ? 'blocked' : 'unblocked'} successfully`);
            setShowBlockModal(false);
            setBlockReason('');
            fetchCustomers();
        } catch (err) {
            setError('Failed to update customer status');
        } finally {
            setActionLoading(false);
        }
    };

    // Stats
    const stats = {
        total: customers.length,
        active: customers.filter(c => c.status !== 'blocked').length,
        blocked: customers.filter(c => c.status === 'blocked').length
    };

    if (loading) {
        return (
            <div className="loading-state">
                <Navbarpro />
                <div className="text-center" style={{ marginTop: '20vh' }}>
                    <Spinner animation="border" />
                    <p className="mt-3">Loading customers...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Navbarpro />

            <Container fluid className="py-4 px-4">
                {/* Page Header */}
                <div className="page-header d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="mb-1">
                            <i className="bi bi-people me-2" style={{ color: 'var(--accent-cyan)' }}></i>
                            Customer Management
                        </h4>
                        <p className="text-secondary mb-0">View and manage all registered customers</p>
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={fetchCustomers}>
                        <i className="bi bi-arrow-clockwise me-1"></i> Refresh
                    </Button>
                </div>

                {/* Alerts */}
                {error && <div className="alert alert-danger">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                {/* Stats */}
                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <div className="stat-card">
                            <div className="d-flex align-items-center gap-3">
                                <div className="icon" style={{ background: 'rgba(0, 212, 255, 0.1)', color: 'var(--accent-cyan)' }}>
                                    <i className="bi bi-people"></i>
                                </div>
                                <div>
                                    <div className="value">{stats.total}</div>
                                    <div className="label">Total Customers</div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="stat-card">
                            <div className="d-flex align-items-center gap-3">
                                <div className="icon" style={{ background: 'rgba(0, 200, 83, 0.1)', color: 'var(--accent-green)' }}>
                                    <i className="bi bi-person-check"></i>
                                </div>
                                <div>
                                    <div className="value">{stats.active}</div>
                                    <div className="label">Active</div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="stat-card">
                            <div className="d-flex align-items-center gap-3">
                                <div className="icon" style={{ background: 'rgba(255, 82, 82, 0.1)', color: 'var(--accent-red)' }}>
                                    <i className="bi bi-person-x"></i>
                                </div>
                                <div>
                                    <div className="value">{stats.blocked}</div>
                                    <div className="label">Blocked</div>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Filters */}
                <Card className="mb-4">
                    <Card.Body>
                        <Row className="align-items-center g-3">
                            <Col md={5}>
                                <InputGroup>
                                    <FormControl
                                        placeholder="Search by name, email, phone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <Button variant="outline-secondary">
                                        <i className="bi bi-search"></i>
                                    </Button>
                                </InputGroup>
                            </Col>
                            <Col md={3}>
                                <DropdownButton
                                    title={statusFilter || "All Statuses"}
                                    variant="outline-secondary"
                                    onSelect={(e) => setStatusFilter(e)}
                                >
                                    <Dropdown.Item eventKey="">All Statuses</Dropdown.Item>
                                    <Dropdown.Item eventKey="active">Active</Dropdown.Item>
                                    <Dropdown.Item eventKey="blocked">Blocked</Dropdown.Item>
                                </DropdownButton>
                            </Col>
                            <Col md={4} className="text-end">
                                <span className="text-secondary">{filteredCustomers.length} customers</span>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Customers Table */}
                {filteredCustomers.length === 0 ? (
                    <div className="empty-state">
                        <i className="bi bi-inbox"></i>
                        <h5>No customers found</h5>
                        <p>Try adjusting your search or filters.</p>
                    </div>
                ) : (
                    <div className="data-table-wrapper">
                        <Table hover className="mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Status</th>
                                    <th>Joined</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((customer, idx) => (
                                    <tr key={customer._id}>
                                        <td>{indexOfFirstItem + idx + 1}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: 36, height: 36,
                                                        background: 'var(--gradient-primary)',
                                                        color: '#000000ff', fontWeight: 600
                                                    }}
                                                >
                                                    {customer.Name?.charAt(0) || 'C'}
                                                </div>
                                                <span>{customer.Name || 'Unknown'}</span>
                                            </div>
                                        </td>
                                        <td>{customer.Email || 'N/A'}</td>
                                        <td>{customer.Phone || 'N/A'}</td>
                                        <td>
                                            <Badge bg={customer.status === 'blocked' ? 'danger' : 'success'}>
                                                {customer.status || 'Active'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {customer.createdAt
                                                ? new Date(customer.createdAt).toLocaleDateString()
                                                : 'N/A'}
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleViewDetails(customer)}
                                                >
                                                    <i className="bi bi-eye"></i>
                                                </Button>
                                                <Button
                                                    variant={customer.status === 'blocked' ? 'outline-success' : 'outline-danger'}
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedCustomer({ customer });
                                                        setShowBlockModal(true);
                                                    }}
                                                >
                                                    <i className={`bi bi-${customer.status === 'blocked' ? 'unlock' : 'lock'}`}></i>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <span className="text-secondary">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCustomers.length)} of {filteredCustomers.length}
                        </span>
                        <Pagination>
                            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} />
                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                                if (pageNum > totalPages) return null;
                                return (
                                    <Pagination.Item key={pageNum} active={currentPage === pageNum} onClick={() => setCurrentPage(pageNum)}>
                                        {pageNum}
                                    </Pagination.Item>
                                );
                            })}
                            <Pagination.Next onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </Container>

            {/* Customer Details Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ background: 'var(--bg-secondary)' }}>
                    <Modal.Title>
                        <i className="bi bi-person-badge me-2"></i>
                        Customer Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCustomer && (
                        <>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <h6 className="text-secondary">Name</h6>
                                    <p>{selectedCustomer.customer?.Name || 'N/A'}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Email</h6>
                                    <p>{selectedCustomer.customer?.Email || 'N/A'}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Phone</h6>
                                    <p>{selectedCustomer.customer?.Phone || 'N/A'}</p>
                                </Col>
                                <Col md={6}>
                                    <h6 className="text-secondary">Status</h6>
                                    <Badge bg={selectedCustomer.customer?.status === 'blocked' ? 'danger' : 'success'}>
                                        {selectedCustomer.customer?.status || 'Active'}
                                    </Badge>
                                </Col>
                            </Row>

                            <Row className="g-3">
                                <Col md={4}>
                                    <div className="metric-card">
                                        <div className="metric-value">{selectedCustomer.stats?.totalOrders || 0}</div>
                                        <div className="metric-label">Total Orders</div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="metric-card">
                                        <div className="metric-value">{selectedCustomer.stats?.totalBookings || 0}</div>
                                        <div className="metric-label">Total Bookings</div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="metric-card">
                                        <div className="metric-value" style={{ color: 'var(--accent-green)' }}>
                                            ₹{selectedCustomer.stats?.totalSpent || 0}
                                        </div>
                                        <div className="metric-label">Total Spent</div>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Block/Unblock Modal */}
            <Modal show={showBlockModal} onHide={() => setShowBlockModal(false)} centered>
                <Modal.Header closeButton style={{ background: 'var(--bg-secondary)' }}>
                    <Modal.Title>
                        {selectedCustomer?.customer?.status === 'blocked' ? 'Unblock' : 'Block'} Customer
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to {selectedCustomer?.customer?.status === 'blocked' ? 'unblock' : 'block'}{' '}
                        <strong>{selectedCustomer?.customer?.Name || 'this customer'}</strong>?
                    </p>
                    {selectedCustomer?.customer?.status !== 'blocked' && (
                        <Form.Group>
                            <Form.Label>Reason for blocking</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={blockReason}
                                onChange={(e) => setBlockReason(e.target.value)}
                                placeholder="Enter reason..."
                            />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBlockModal(false)}>Cancel</Button>
                    <Button
                        variant={selectedCustomer?.customer?.status === 'blocked' ? 'success' : 'danger'}
                        onClick={handleBlockCustomer}
                        disabled={actionLoading}
                    >
                        {actionLoading ? <Spinner size="sm" /> :
                            selectedCustomer?.customer?.status === 'blocked' ? 'Unblock' : 'Block'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default CustomerManagement;
